/**
 * attributeHistory — pure shaping helpers for the Verlauf panel's
 * transitions log + per-attribute value-history view (#1913 C).
 *
 * The heavy lifting (numeric-vs-categorical detection, chart-data
 * shaping, German change captions) lives here so it is unit-testable
 * without React and so recharts (which does not render under jsdom)
 * stays out of the logic path.
 */
import type {
  AttributeTransition,
  EntitySeriesGroup,
  EntitySourceDoc,
  LenseField,
} from '../../../services/twinService';
import { formatScalar, prettifyKey } from '../formatValue';

/** Ontology types that are unambiguously numeric (chart-worthy). */
const NUMERIC_TYPE_RE = /^(float|int|double|decimal|number|currency)/;
/** Ontology types that are never charted (force the categorical list). */
const CATEGORICAL_TYPE_RE = /^(string|bool|boolean|timestamp|date|enum|json|uuid)/;

/**
 * Coerce a JSON value to a finite number, or ``null`` when it isn't one.
 * Numeric strings ("5.6") count — structured mappers often send numbers
 * as strings — but booleans, objects and blank strings do not.
 */
export function coerceNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** The German label for an attribute — the ontology field name wins. */
export function attributeLabel(attribute: string, field?: LenseField): string {
  return field?.name ?? prettifyKey(attribute);
}

/** The single time dimension of a series group (``valid_from`` etc.). */
export function groupTime(group: EntitySeriesGroup): string | null {
  // ``key`` is optional-chained like the sibling ``measures?.value`` — a
  // malformed group missing it must not throw (codex round-2).
  const values = Object.values(group.key ?? {});
  return values.length > 0 ? values[0] : null;
}

export interface SeriesPoint {
  /** ISO time the attribute took this value (write time). */
  t: string;
  value: unknown;
  /** ``true`` when the attribute was removed at ``t`` (not an explicit null). */
  absent: boolean;
  /** Provenance document id where the recorder had one. */
  sourceId: string | null;
}

/** Project the series envelope's groups onto flat points (ascending). */
export function seriesPoints(groups: EntitySeriesGroup[]): SeriesPoint[] {
  const points: SeriesPoint[] = [];
  for (const group of groups) {
    const t = groupTime(group);
    if (!t) continue;
    points.push({
      t,
      value: group.measures?.value,
      absent: Boolean(group.value_absent),
      sourceId: group.source_id ?? null,
    });
  }
  return points;
}

/**
 * Whether an attribute's value series should render as a numeric chart
 * (vs a categorical dated list). The ontology type wins when it is
 * decisive. WITHOUT a numeric field type we only chart actual ``number``
 * values — never numeric-looking STRINGS: this mirrors ``formatScalar``,
 * which deliberately refuses to parse numeric strings without a schema
 * so identifiers like a lot/parcel number "01070654" (which arrive as
 * strings) stay categorical instead of being charted (codex round-1 P2).
 */
export function isNumericSeries(points: SeriesPoint[], field?: LenseField): boolean {
  const present = points.filter((p) => !p.absent);
  if (present.length === 0) return false;
  const type = field?.type;
  if (type && NUMERIC_TYPE_RE.test(type)) return present.some((p) => coerceNumber(p.value) !== null);
  if (type && CATEGORICAL_TYPE_RE.test(type)) return false;
  // Schema-poor: only true numbers count (no numeric-string parsing).
  return present.every((p) => typeof p.value === 'number' && Number.isFinite(p.value));
}

export interface ChartPoint {
  t: string;
  /**
   * The numeric value at ``t``, or ``null`` for a GAP — a removed
   * (``absent``) point or a present-but-non-numeric value. ``null`` +
   * ``connectNulls={false}`` breaks the line so it never implies the
   * value persisted across a removal, and never silently omits a stray
   * non-numeric point in an otherwise-numeric series (codex round-2).
   */
  value: number | null;
  /** Provenance document id for the point (drives the preview thread). */
  sourceId: string | null;
  /** Safe provenance label ("Beleg 2024"), never a raw id; null = none. */
  provenance: string | null;
}

/**
 * The recharts line points, ascending, ONE per series point (never
 * dropped): a present coercible value becomes a number; a removed
 * (``absent``) or present-but-non-coercible value becomes ``null`` (a
 * gap, rendered with ``connectNulls={false}``) so the line agrees with
 * the categorical branch on removals and never misrepresents the series
 * by closing over a hole (codex round-2). Each point carries its safe
 * provenance label so the tooltip conveys the "source doc → year"
 * meaning of a backfill-day cluster (never a raw id).
 */
export function seriesChartData(
  points: SeriesPoint[],
  docsById?: Map<string, EntitySourceDoc>,
): ChartPoint[] {
  return points.map((p) => ({
    t: p.t,
    value: p.absent ? null : coerceNumber(p.value),
    sourceId: p.sourceId,
    provenance: provenanceLabel(p.sourceId, docsById),
  }));
}

/**
 * Extract the four-digit year of a source document — from the
 * ``document_date`` ISO prefix (avoids the timezone-off-by-one a
 * ``Date`` parse of a bare ``YYYY-MM-DD`` risks), else a lenient parse.
 * ``null`` when the doc or its date is missing/unparseable.
 */
export function provenanceYear(doc?: EntitySourceDoc): string | null {
  const raw = doc?.document_date;
  if (!raw) return null;
  const prefix = /^(\d{4})-\d{2}-\d{2}/.exec(raw);
  if (prefix) return prefix[1];
  // Lenient fallback stays ISO-consistent: read the UTC year (all twin
  // timestamps are UTC), never the local-zone year a bare getFullYear()
  // would give a UTC+1/+2 audience near a year boundary (codex round-2).
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : String(parsed.getUTCFullYear());
}

/**
 * The safe, human provenance label for a transition/series point:
 * "Beleg 2024" when the linked source document's year is known, plain
 * "Beleg" when the linked document exists without a resolvable year, and
 * ``null`` when the source resolves to NO linked document. NEVER exposes a
 * raw id.
 *
 * The source resolving to a real document is REQUIRED (``docsById.has``),
 * not incidental. A change can carry a provenance marker that is not a
 * document — a bulk backfill stamps every transition's ``source_id`` with a
 * synthetic marker (``wiberry-mysql-backfill``) so the audit trail records
 * what produced the change, but there is no ``documents`` row behind it.
 * Labelling that "Beleg" rendered a receipt chip the farmer could click but
 * never open (#2076). ``docsById`` is the entity's LINKED source docs — the
 * same set the Belege panel trusts — so this makes the Verlauf agree with
 * it: no linked document, no Beleg.
 */
export function provenanceLabel(sourceId: string | null, docsById?: Map<string, EntitySourceDoc>): string | null {
  if (!sourceId) return null;
  const doc = docsById?.get(sourceId);
  if (!doc) return null;
  const year = provenanceYear(doc);
  return year ? `Beleg ${year}` : 'Beleg';
}

const MAX_JSON_LEN = 80;

/** Compact, length-capped rendering of a non-scalar JSON value. */
function compactJson(value: unknown): string {
  let text: string;
  try {
    text = JSON.stringify(value);
  } catch {
    text = String(value);
  }
  if (text.length > MAX_JSON_LEN) return `${text.slice(0, MAX_JSON_LEN - 1)}…`;
  return text;
}

/**
 * Human-readable rendering of one attribute value at a point in time.
 * ``absent`` (the attribute was removed) reads as "entfernt"; an explicit
 * JSON ``null`` reads as "leer"; a string the card labels (``valueLabels`` —
 * this attribute's slice of the card's value-label projection, HOF-696 D20:
 * ``enabled`` → "freigegeben") reads as its label, an unknown one stays raw;
 * scalars go through the shared property formatter (German number/date/bool
 * formatting, unit suffixes); objects/arrays fall back to compact JSON.
 */
export function formatSeriesValue(
  value: unknown,
  absent: boolean,
  attribute: string,
  field?: LenseField,
  valueLabels?: Record<string, string>,
): string {
  if (absent) return 'entfernt';
  if (value === null || value === undefined) return 'leer';
  if (typeof value === 'object') return compactJson(value);
  const label = typeof value === 'string' ? valueLabels?.[value] : undefined;
  if (label) return label;
  return formatScalar(value, attribute, field);
}

export type TransitionMode = 'set' | 'removed' | 'changed';

export interface TransitionDescription {
  attribute: string;
  label: string;
  mode: TransitionMode;
  oldText: string;
  newText: string;
}

/**
 * Describe one transition for the log row: which attribute changed, its
 * German label, whether it was set/removed/changed, and the formatted
 * old/new values. ``old_value_absent``/``new_value_absent`` (the SQL-side
 * IS NULL flags) decide set-vs-changed and removed — never the decoded
 * value, which is ambiguous for an explicit JSON ``null``.
 */
export function describeTransition(
  t: AttributeTransition,
  field?: LenseField,
  valueLabels?: Record<string, string>,
): TransitionDescription {
  const label = attributeLabel(t.attribute, field);
  // ``oldText``/``newText`` are the plain formatted values, empty on the
  // absent side; the appeared/removed framing is the row's job (via
  // ``mode``) so "gesetzt"/"entfernt" don't collide with a real value.
  const oldText = t.old_value_absent ? '' : formatSeriesValue(t.old_value, false, t.attribute, field, valueLabels);
  const newText = t.new_value_absent ? '' : formatSeriesValue(t.new_value, false, t.attribute, field, valueLabels);
  let mode: TransitionMode;
  if (t.old_value_absent) mode = 'set';
  else if (t.new_value_absent) mode = 'removed';
  else mode = 'changed';
  return { attribute: t.attribute, label, mode, oldText, newText };
}
